import { useCallback, useEffect, useMemo, useState, useRef, Fragment } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RefreshCw, AlertCircle, Save, Trash2, ChevronDown, Pencil, Rows3, LayoutGrid, FileUp, History } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";
import { useAuth } from "@/context/AuthContext";
import PayrollEmployeeProfile from "@/components/dashboard/PayrollEmployeeProfile";
import VacationBankHistoryDialog from "@/components/dashboard/VacationBankHistoryDialog";
import ImportedStubCard from "@/components/dashboard/payroll/ImportedStubCard";
import { useStubDrop, StubDropOverlay, importStubsInBatches } from "@/components/dashboard/payroll/stubDrop";
import { StubImportProgress } from "@/components/dashboard/payroll/StubImportProgress";
import DiscrepancyDialog from "@/components/dashboard/payroll/DiscrepancyDialog";
import ImportStubsPrompt from "@/components/dashboard/payroll/ImportStubsPrompt";

/** Monday of the week containing d. */
function mondayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + (x.getDay() === 0 ? -6 : 1 - x.getDay()));
  return x;
}
const toISO = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const fromISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtRange = (mon) => {
  const opts = { month: "short", day: "numeric" };
  return `${mon.toLocaleDateString("en-US", opts)} – ${addDays(mon, 6).toLocaleDateString("en-US", { ...opts, year: "numeric" })}`.toUpperCase();
};
const money = (n) => { const v = Math.abs(Number(n || 0)) < 0.005 ? 0 : Number(n || 0); return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }; // -0.00 never shows
const num = (v) => (v === "" || v === null || v === undefined) ? 0 : (parseFloat(v) || 0);
/** The most that can be paid out THIS pay without the bank going negative:
 * confirmed bank + this week's accrual (earned on this very cheque) − any
 * auto "paid on each cheque" accrual. Same figure the Bank $ cell is built
 * from, so the `!` check, the pre-fill cap and the server all agree. */
const payableBank = (row) => row.vacation_payable !== undefined
  ? Number(row.vacation_payable || 0)
  : Number(row.vacation_available_before || 0) + Number(row.vacation_pay_accrued || 0) - Number(row.vacation_auto_paid || 0);

/** Client-side gross calc so the Gross column updates instantly as you type.
 * Server confirms the exact number on Save. */
function calcGross(row, edits) {
  const rate = Number(row.hourly_rate || 0);
  const baseWages = row.pay_type === "salary"
    ? Number(row.weekly_salary || 0)
    : Number(row.regular_pay || 0) + Number(row.overtime_pay || 0);
  // Manual vacation payout comes from the editable cells; auto-paid vacation
  // (opted-out waiver / "Paid on each pay check") is added server-side, so
  // include it here or the live gross won't match the server's.
  const vac = num(edits.vac_hrs) * rate + num(edits.vac_dollars)
    + Number(row.vacation_auto_paid || 0);
  // Stat holiday pay comes from the server (auto-computed from the
  // timesheet's holiday_days + hours override × base rate).
  const stat = Number(row.stat_holiday_pay || 0);
  // Taxed recurring income lines are part of gross on the server too.
  const recurring = (row.recurring_income_lines || [])
    .filter((l) => l.taxed)
    .reduce((s, l) => s + Number(l.dollars || 0), 0);
  return baseWages + vac + stat + recurring + num(edits.holiday) + num(edits.cra);
}

/** Banked vacation cell — displays the LIVE figure: confirmed bank + this
 * week's ongoing accrual − this week's payout. Click to edit the confirmed
 * balance; the number typed becomes the new baseline at that moment (weeks
 * before the change never move it, this pay week onward adds accrual). */
const VacBankCell = ({ employeeId, value, accrued = 0, paidOut = 0, canEdit, onSaved }) => {
  const [text, setText] = useState(null); // null = showing live figure, not editing
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const live = Number(value || 0) + Number(accrued || 0) - Number(paidOut || 0);
  if (!canEdit) return <>{money(live)}</>;
  if (text === null) {
    return (
      <button
        type="button"
        onClick={() => setText(Number(value || 0).toFixed(2))}
        title={`Live banked vacation: ${money(value)} confirmed + ${money(accrued)} accruing this week − ${money(paidOut)} paid out this week. Click to set a new confirmed balance — it becomes the baseline right now and only this pay week onward adds on top.`}
        aria-label="Banked vacation pay (live — click to edit the confirmed balance)"
        data-testid={`vac-bank-edit-${employeeId}`}
        className="w-24 rounded border border-emerald-500/30 bg-white/5 px-1 py-0.5 text-center font-mono text-emerald-800 hover:border-[#1a3fd1] focus:outline-none dark:text-emerald-300"
      >
        {money(live)}
      </button>
    );
  }
  return (
    <input
      type="text"
      inputMode="decimal"
      autoFocus
      value={text}
      onChange={(ev) => {
        const v = ev.target.value.replace(/[^0-9.]/g, "");
        setText(v);
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
          try {
            const res = await api.put(`/payroll/vacation/${employeeId}/bank`, { available: parseFloat(v) || 0 });
            onSaved(employeeId, res.data.available, res.data.available_before_week);
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not save the vacation bank");
          }
        }, 700);
      }}
      onBlur={() => setText(null)}
      aria-label="Banked vacation pay (editing confirmed balance)"
      title="Editing the confirmed balance — saves automatically; the typed number becomes the baseline right now"
      data-testid={`vac-bank-edit-${employeeId}`}
      className="w-24 rounded border border-[#1a3fd1] bg-white/5 px-1 py-0.5 text-center font-mono text-emerald-800 focus:outline-none dark:text-emerald-300"
    />
  );
};

const PayrollTab = () => {
  const { user } = useAuth();
  const canAdmin = ["owner", "superadmin", "admin"].includes(user?.role);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastWeeks, setPastWeeks] = useState([]);
  const [govOwed, setGovOwed] = useState({ total: 0, breakdown: {}, paystubs: 0, last_payment: null });
  const [profileId, setProfileId] = useState(null); // Knit-style employee profile overlay
  // Per-row edits keyed by employee_id: {vac_hrs, vac_dollars, holiday, cra}
  const [edits, setEdits] = useState({});
  const [expanded, setExpanded] = useState(null); // employee_id whose row is expanded
  // View mode: "auto" (responsive — cards on phones, grid on tablets/laptops),
  // remembered per-user once toggled. Mirrors the Employee Hours tab.
  const viewStorageKey = `payroll-view-mode:${user?.user_id || "anon"}`;
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(viewStorageKey) || "auto"; } catch { return "auto"; }
  });
  useEffect(() => {
    try {
      if (viewMode === "auto") localStorage.removeItem(viewStorageKey);
      else localStorage.setItem(viewStorageKey, viewMode);
    } catch { /* ignore quota / private mode */ }
  }, [viewMode, viewStorageKey]);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const on = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const effectiveMode = viewMode === "auto" ? (isNarrow ? "cards" : "grid") : viewMode;
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const weekISO = useMemo(() => toISO(weekStart), [weekStart]);
  const maxWeekISO = useMemo(() => toISO(addDays(mondayOf(new Date()), 7)), []);
  const canGoNext = weekISO < maxWeekISO;

  const [capReport, setCapReport] = useState([]);
  const [capOpen, setCapOpen] = useState(false); // 8h cap box starts collapsed
  const [vacRequests, setVacRequests] = useState([]); // pending vacation-pay payout requests for this week
  const [vacHistory, setVacHistory] = useState(null); // { employee_id, employee_name } → bank history dialog
  const [bulkBusy, setBulkBusy] = useState(false);      // Knit bulk stub import in flight
  const [bulkResults, setBulkResults] = useState(null); // per-file import outcome
  const bulkFileRef = useRef(null);
  const bulkFilesRef = useRef([]);                      // File objects, kept so unmatched stubs can be assigned manually
  const [bulkEmps, setBulkEmps] = useState([]);         // employee list for the manual-assign pickers
  const [assignSel, setAssignSel] = useState({});       // row index → chosen employee_id
  const [assigning, setAssigning] = useState({});       // row index → busy flag
  const [viewImport, setViewImport] = useState(null);   // import_id → readable stub card
  const [bulkProgress, setBulkProgress] = useState(null); // {done,total,pct,current} while importing
  const [discKey, setDiscKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, weeksRes, govRes, capRes, vacRes] = await Promise.all([
        api.get(`/payroll/summary?week_start=${weekISO}`),
        api.get("/payroll/weeks"),
        api.get("/payroll/gov-owed"),
        api.get(`/payroll/cap-report?week_start=${weekISO}`).catch(() => ({ data: { employees: [] } })),
        api.get("/vacation-requests").catch(() => ({ data: [] })), // ALL pending payout requests (any pay week)
      ]);
      const data = sumRes.data || [];
      setRows(data);
      setPastWeeks(weeksRes.data || []);
      setGovOwed(govRes.data || { total: 0 });
      setCapReport((capRes.data || {}).employees || []);
      setVacRequests(vacRes.data || []);
      // Seed edits from server so already-saved paystubs show their values
      const seeded = {};
      data.forEach((r) => {
        seeded[r.employee_id] = {
          vac_hrs: r.vacation_hours_paid ? String(r.vacation_hours_paid) : "",
          vac_dollars: r.vacation_dollars_paid ? String(r.vacation_dollars_paid) : "",
          holiday: r.holiday_pay ? String(r.holiday_pay) : "",
          cra: r.cra_repayment ? String(r.cra_repayment) : "",
          rate_choice: r.rate_choice_applied || "base",
        };
      });
      setEdits(seeded);
      // a (re)load re-seeds the fields from the server — let the pending
      // vacation requests pre-fill again for this week
      autoFilledWeek.current = "";
    } catch {
      toast.error("Could not load payroll");
    } finally {
      setLoading(false);
    }
  }, [weekISO]);

  useEffect(() => { load(); }, [load]);

  // Debounced live recalc → keeps NET / GOV columns in sync with edits.
  // Each row has its own timer so edits to one row don't delay another.
  const timers = useRef({});
  const errAt = useRef({}); // throttle recalc error toasts per employee
  // editsRef always holds the LATEST edits — the debounced timer reads from
  // it because the `edits` state in this closure is one render behind the
  // keystroke that scheduled it (setEdits is async). Without this, every
  // recalc sent the PREVIOUS edit's values and the totals lagged one edit.
  const editsRef = useRef({});
  useEffect(() => { editsRef.current = edits; }, [edits]);
  const [calcBusy, setCalcBusy] = useState({}); // employee_id -> bool
  const recalcRow = useCallback((eid) => {
    if (timers.current[eid]) clearTimeout(timers.current[eid]);
    // Mark busy at schedule time (not when the fetch fires) so the header
    // totals show their working indicator during the debounce window too.
    setCalcBusy((s) => ({ ...s, [eid]: true }));
    timers.current[eid] = setTimeout(async () => {
      const row = rows.find((r) => r.employee_id === eid);
      if (!row) { setCalcBusy((s) => ({ ...s, [eid]: false })); return; }
      const e = editsRef.current[eid] || {};
      try {
        const { data } = await api.post("/payroll/calc", {
          employee_id: eid,
          week_start: weekISO,
          vacation_hours_paid: num(e.vac_hrs),
          vacation_dollars_paid: num(e.vac_dollars),
          holiday_pay: num(e.holiday),
          cra_repayment: num(e.cra),
          rate_choice: e.rate_choice || row.rate_choice_applied || "base",
        });
        setRows((prev) => prev.map((r) =>
          r.employee_id === eid
            ? { ...r, ...data,
                // keep the as-loaded choice so unsaved-change detection stays
                // stable (the recalc echoes the NEW pick back)
                rate_choice: r.rate_choice,
                rate_choice_applied: r.rate_choice_applied }
            : r));
      } catch {
        // Surface a failed recalc instead of leaving the totals silently stale
        const now = Date.now();
        if (now - (errAt.current[eid] || 0) > 5000) {
          errAt.current[eid] = now;
          toast.error(`Could not recalculate ${row.employee_name}'s net — totals may be stale`);
        }
      }
      finally { setCalcBusy((s) => ({ ...s, [eid]: false })); }
    }, 500);
  }, [rows, weekISO]);

  const editRow = (eid, field, val) => {
    setEdits((prev) => ({
      ...prev,
      [eid]: { ...(prev[eid] || {}), [field]: val.replace(/[^0-9.]/g, "") },
    }));
    recalcRow(eid);
  };

  // One tap on the red "!" → payout becomes exactly what's banked (live bank:
  // confirmed + this week's accrual). Dollars only, hours cleared, so the
  // bank lands at $0.00 and the week can be saved.
  const setToBanked = (row) => {
    const bank = Math.max(0, payableBank(row));
    setEdits((prev) => ({ ...prev, [row.employee_id]: { ...(prev[row.employee_id] || {}), vac_dollars: bank > 0 ? bank.toFixed(2) : "", vac_hrs: "" } }));
    recalcRow(row.employee_id);
    toast.success(`${row.employee_name}: vacation payout set to banked ${money(bank)}`);
  };

  const rowGross = (row) => calcGross(row, edits[row.employee_id] || {});
  const rowPayout$ = (row) => num((edits[row.employee_id] || {}).vac_hrs) * Number(row.hourly_rate || 0)
    + num((edits[row.employee_id] || {}).vac_dollars);

  /** Employee has a perfect-week bonus rate configured (and it differs from
   * their set base rate) → the payroll card shows a per-week rate picker.
   * Nothing auto-decides: new weeks default to the base rate and the admin
   * explicitly picks which amount to pay. */
  const hasPerfectRate = (r) => Number(r.hourly_rate_perfect || 0) > 0
    && Math.abs(Number(r.hourly_rate_perfect || 0) - Number(r.hourly_rate_base || 0)) > 0.004;

  // Rate picks AUTO-SAVE the moment they are made — no save button needed.
  // The pick is written to that week's paystub AND stored as the employee's
  // standing default for future unsaved weeks (backend does both). Past
  // saved weeks are never touched: each stub keeps its own recorded choice.
  const [rateSaving, setRateSaving] = useState(null); // employee_id currently saving
  const setRateChoice = (eid, val) => {
    setEdits((prev) => ({ ...prev, [eid]: { ...(prev[eid] || {}), rate_choice: val } }));
    recalcRow(eid);
    (async () => {
      const e = editsRef.current[eid] || {};
      setRateSaving(eid);
      try {
        await api.post("/payroll/paystub", {
          employee_id: eid,
          week_start: weekISO,
          vacation_hours_paid: num(e.vac_hrs),
          vacation_dollars_paid: num(e.vac_dollars),
          holiday_pay: num(e.holiday),
          cra_repayment: num(e.cra),
          rate_choice: val,
        });
        // refresh just this row from server truth (saved stub + choice)
        const { data } = await api.get("/payroll/summary", { params: { week_start: weekISO } });
        const fresh = data.find((x) => x.employee_id === eid);
        if (fresh) {
          setRows((prev) => prev.map((r) => (r.employee_id === eid ? fresh : r)));
          setEdits((prev) => ({ ...prev, [eid]: { ...(prev[eid] || {}), rate_choice: fresh.rate_choice_applied || val } }));
        }
        toast.success(`Rate saved for ${rows.find((r) => r.employee_id === eid)?.employee_name || "employee"} — this week and future weeks`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't save the rate change");
        load(); // revert the picker to what's actually stored
      } finally {
        setRateSaving(null);
      }
    })();
  };

  // Auto-fill pending vacation payout requests into the (unsaved) vac-dollars
  // field when a week is opened — the owner just reviews and hits save. Only
  // fills fields that are currently zero so manual edits are never clobbered.
  const autoFilledWeek = useRef("");
  useEffect(() => {
    if (!rows.length || !vacRequests.length) return;
    if (autoFilledWeek.current === weekISO) return; // once per week view
    autoFilledWeek.current = weekISO;
    const fill = {};
    const clamped = [];
    for (const r of vacRequests) {
      if (r.week_start !== weekISO) continue; // only requests targeting THIS pay week
      const row = rows.find((x) => x.employee_id === r.employee_id);
      if (!row || row.vacation_exempt) continue;
      const existing = edits[r.employee_id] || {};
      if (num(existing.vac_dollars) || num(existing.vac_hrs) || Number(row.vacation_dollars_paid || 0) || Number(row.vacation_hours_paid || 0)) continue;
      // Cap at the LIVE bank (confirmed + this week's accrual) — the same
      // figure the Bank $ cell shows. The bank can't go negative, but this
      // week's accrual IS payable on this cheque.
      const bank = Math.max(0, payableBank(row));
      const amt = Math.min(Number(r.amount || 0), bank);
      if (amt < Number(r.amount || 0) - 0.005) clamped.push(`${r.employee_name}: asked ${money(r.amount)}, banked ${money(bank)}`);
      if (amt <= 0) continue;
      // ONE field only: an hours request fills Vac hrs (payroll prices it at
      // the pay rate); a dollar request fills Vac $. Never both.
      if (Number(r.hours || 0) > 0 && amt >= Number(r.amount || 0) - 0.005) {
        fill[r.employee_id] = { ...existing, vac_hrs: Number(r.hours).toFixed(2), vac_dollars: "" };
      } else {
        fill[r.employee_id] = { ...existing, vac_dollars: amt.toFixed(2), vac_hrs: "" };
      }
    }
    if (clamped.length) toast.warning("Vacation request exceeds banked balance — pre-filled the banked amount instead", { description: clamped.join(" · "), duration: 10000 });
    if (!Object.keys(fill).length) return;
    setEdits((prev) => ({ ...prev, ...fill }));
    Object.keys(fill).forEach((eid) => recalcRow(eid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, vacRequests, weekISO]);
  const overVac = (row) => {
    const p = rowPayout$(row);
    // only a real payout can overdraw — a $0 payout on a negative banked
    // balance (legacy data) must never block saving the week. The limit is
    // the LIVE bank (confirmed + this week's accrual), exactly what the
    // Bank $ cell is built from — if that cell is ≥ $0 the payout is fine.
    return p > 0.005 && p > payableBank(row) + 0.01;
  };

  const anyChanges = useMemo(() =>
    rows.some((r) => {
      const e = edits[r.employee_id] || {};
      return num(e.vac_hrs) !== Number(r.vacation_hours_paid || 0)
        || num(e.vac_dollars) !== Number(r.vacation_dollars_paid || 0)
        || num(e.holiday) !== Number(r.holiday_pay || 0)
        || num(e.cra) !== Number(r.cra_repayment || 0)
        || (e.rate_choice || "base") !== (r.rate_choice_applied || "base");
    }), [rows, edits]);

  const anyOver = rows.some((r) => overVac(r));

  const saveAll = async () => {
    if (anyOver) {
      toast.error("One or more employees have a vacation payout above their banked balance.", { description: "Tap the red ! on that row to set the payout to exactly what's banked." });
      return;
    }
    setSaving(true);
    try {
      await Promise.all(rows.map((r) => {
        const e = edits[r.employee_id] || {};
        return api.post("/payroll/paystub", {
          employee_id: r.employee_id,
          week_start: weekISO,
          vacation_hours_paid: num(e.vac_hrs),
          vacation_dollars_paid: num(e.vac_dollars),
          holiday_pay: num(e.holiday),
          cra_repayment: num(e.cra),
          rate_choice: e.rate_choice || r.rate_choice_applied || "base",
        });
      }));
      toast.success("Payroll saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save payroll");
    } finally {
      setSaving(false);
    }
  };

  const markCraPaid = async () => {
    const ok = await confirm({
      title: "Mark the CRA balance as paid?",
      description: `This records a remittance payment of ${money(govOwed.total)} to the CRA and resets the owed total to $0. It will build up again only when you confirm and save payroll.`,
      confirmLabel: "Yes, mark paid",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    try {
      const res = await api.post("/payroll/gov-owed/mark-paid");
      setGovOwed(res.data);
      toast.success("CRA balance marked paid — owed total reset to $0");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark the CRA balance paid");
    }
  };

  const deleteStub = async (row) => {
    if (!row.paystub_id) return;
    const ok = await confirm({
      title: `Delete ${row.employee_name}'s paystub?`,
      description: "This paystub will be removed and can be re-saved later.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/payroll/paystub/${row.paystub_id}`);
      toast.success("Paystub deleted");
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const deleteVacRequest = async (r) => {
    const ok = await confirm({
      title: `Delete ${r.employee_name}'s vacation request?`,
      description: `The ${money(r.amount)} request is removed and its yellow pre-fill disappears. Any vacation payout amount you already saved on their row stays — clear it manually if needed.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/vacation-requests/${r.request_id}`);
      toast.success("Vacation request deleted");
      await load();
    } catch { toast.error("Could not delete the request"); }
  };

  // Bulk Knit pay-stub import (PDF or CSV): each file is scanned and
  // auto-matched to an employee by the name printed inside — inactive
  // employees included; results show per file. Unmatched files keep their
  // File handle so the admin can assign them manually. Fed by the file
  // picker AND by dropping files/folders anywhere on the page.
  const importStubFiles = useCallback(async (files) => {
    if (!files.length) return;
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: files.length, pct: 0, current: [] });
    try {
      const res = { data: await importStubsInBatches(api, files, setBulkProgress) };
      bulkFilesRef.current = files;
      setBulkResults(res.data);
      setAssignSel({});
      const { imported, duplicates = 0, total } = res.data;
      const blanks = res.data.results.filter((r) => r.status === "imported" && r.blank).length;
      if (imported + duplicates === total) {
        toast.success(`Imported ${imported} pay stub${imported === 1 ? "" : "s"}${duplicates ? ` · ${duplicates} already on file, skipped` : ""}`);
      } else {
        toast.warning(`Imported ${imported} of ${total}${duplicates ? ` (${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped)` : ""} — pick an employee for the rest below`);
      }
      if (blanks) toast.info(`${blanks} blank $0 stub${blanks === 1 ? "" : "s"} flagged`);
      // Manual assignment needs the employee list — lazy-load it once.
      // Inactive people stay in the picker: legacy stubs are mostly theirs.
      if (res.data.results.some((r) => r.status === "unmatched") && bulkEmps.length === 0) {
        try {
          const er = await api.get("/employees");
          const list = Array.isArray(er.data) ? er.data : er.data.employees || [];
          setBulkEmps(list.filter((e) => e.status !== "Deleted")
            .sort((a, b) => ((a.status || "Active") === "Active" ? 0 : 1) - ((b.status || "Active") === "Active" ? 0 : 1) || (a.name || "").localeCompare(b.name || "")));
        } catch { /* picker stays empty; admin can use the profile import */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
      setTimeout(() => setBulkProgress(null), 1200);
      setDiscKey((k) => k + 1); // re-check Knit-vs-app discrepancies after an import
    }
  }, [bulkEmps.length]);

  const bulkImport = (ev) => {
    const files = Array.from(ev.target.files || []);
    ev.target.value = "";
    importStubFiles(files);
  };

  const onDroppedStubs = useCallback((files) => {
    if (!files.length) { toast.error("No PDF or CSV pay stubs in that drop"); return; }
    importStubFiles(files);
  }, [importStubFiles]);
  const { dragging: stubDragging, dropProps: stubDropProps } = useStubDrop(onDroppedStubs, { disabled: !canAdmin || bulkBusy || !!profileId });

  /** Manually assign an unmatched stub file to a chosen employee. */
  const assignBulkStub = async (i) => {
    const f = bulkFilesRef.current[i];
    const employeeId = assignSel[i];
    if (!f || !employeeId) return;
    const empName = bulkEmps.find((e) => e.employee_id === employeeId)?.name || "employee";
    setAssigning((s) => ({ ...s, [i]: true }));
    try {
      const fd = new FormData();
      fd.append("file", f);
      // confirm_mismatch: the whole point is the stub name didn't match anyone.
      const r = await api.post(`/payroll/imported-paystubs/${employeeId}?confirm_mismatch=true`, fd);
      const dup = !!r.data.duplicate;
      setBulkResults((prev) => {
        const results = prev.results.map((row, idx) => idx !== i ? row : {
          ...row, status: dup ? "duplicate" : "imported", label: r.data.label, gross: r.data.gross,
          blank: r.data.blank, import_id: r.data.import_id,
          detail: dup ? "Already imported — not added again" : row.detail,
          employee_name: empName, employee_id: employeeId, manually_assigned: true,
        });
        return { ...prev, results, imported: results.filter((x) => x.status === "imported").length,
                 duplicates: results.filter((x) => x.status === "duplicate").length };
      });
      if (dup) toast.info(`${r.data.label} is already on file for ${empName} — not added again`);
      else toast.success(`${r.data.label} assigned to ${empName}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import that stub");
    } finally {
      setAssigning((s) => ({ ...s, [i]: false }));
    }
  };

  const weekTotals = useMemo(
    () => rows.reduce(
      (t, r) => ({
        gross: t.gross + rowGross(r),
        net: t.net + Number(r.net_pay || 0),
        gov: t.gov + Number(r.gov_remittance || 0),
        cpp: t.cpp + Number(r.cpp_employee || 0) + Number(r.cpp_employer || 0) + Number(r.cpp2_employee || 0) + Number(r.cpp2_employer || 0),
        ei: t.ei + Number(r.ei_employee || 0) + Number(r.ei_employer || 0),
        fed: t.fed + Number(r.federal_tax || 0),
        on: t.on + Number(r.provincial_tax || 0),
        saved: t.saved + (r.saved ? 1 : 0),
      }),
      { gross: 0, net: 0, gov: 0, cpp: 0, ei: 0, fed: 0, on: 0, saved: 0 },
    ),
    [rows, edits],
  );
  // Net/Gov ride on the debounced server recalc — show a working indicator
  // in the header pills while any row is mid-recalc so the totals never
  // look frozen right after typing.
  const recalculating = rows.some((r) => calcBusy[r.employee_id]);

  /** Paystub breakdown shown when a row (grid) or card is expanded.
   * Shared so both layouts show the exact same detail. */
  const renderStubDetails = (r, gross) => (
    <div data-testid={`paystub-details-${r.employee_id}`}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <DetailBox label="Employee net" value={money(r.net_pay)} tone="emerald" testid={`net-pay-${r.employee_id}`} />
        <DetailBox label="Total gross" value={money(gross)} tone="white" />
        <DetailBox label="Owed to gov" value={money(r.gov_remittance)} tone="amber" testid={`gov-remit-${r.employee_id}`} />
        <DetailBox label="Vacation accrued (this stub)" value={money(r.vacation_pay_accrued)} tone="emerald" />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
        <Line
          label={r.hourly_rate > 0
            ? `Regular pay (${r.regular_hours}h @ $${Number(r.hourly_rate).toFixed(2)}/h)`
            : "Regular pay"}
          v={r.regular_pay}
        />
        <Line label={`Overtime (${r.overtime_hours}h)`} v={r.overtime_pay} />
        {r.stat_holiday_pay > 0 && (
          <Line label={`Stat holiday (${Number(r.stat_holiday_hours_total || 0).toFixed(2)}h)`} v={r.stat_holiday_pay} tone="emerald" />
        )}
        <Line label="CPP employee" v={r.cpp_employee} tone="red" />
        <Line label="CPP2 employee" v={r.cpp2_employee} tone="red" />
        <Line label="EI employee" v={r.ei_employee} tone="red" />
        <Line label="Federal tax" v={r.federal_tax} tone="red" />
        <Line label="Provincial (ON) tax" v={r.provincial_tax} tone="red" />
        <Line label="Employer CPP + EI" v={(r.cpp_employer||0)+(r.ei_employer||0)+(r.cpp2_employer||0)} tone="amber" />
      </div>
      {r.saved && (
        <div className="mt-3 flex items-center justify-between">
          <p className="font-mono text-[9px] uppercase tracking-widest text-emerald-400/70">
            Saved paystub · {r.paystub_id}
          </p>
          <button type="button" onClick={() => deleteStub(r)}
            data-testid={`paystub-delete-${r.employee_id}`}
            className="inline-flex items-center gap-1.5 rounded border border-red-500/30 bg-red-500/5 px-3 py-1.5 text-xs font-semibold text-red-300 hover:border-red-500/50">
            <Trash2 className="h-3.5 w-3.5" /> Delete stub
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="relative space-y-4 p-4 sm:p-6" data-testid="payroll-tab" {...stubDropProps}>
      <StubDropOverlay show={stubDragging} testId="payroll-stub-drop-overlay" />
      <StubImportProgress progress={bulkProgress} testId="payroll-import-progress" />
      {["owner", "superadmin", "admin"].includes(user?.role) && <DiscrepancyDialog refreshKey={discKey} />}
      {canAdmin && <ImportStubsPrompt onChooseFiles={() => bulkFileRef.current?.click()} onFiles={importStubFiles} busy={bulkBusy} />}
      {viewImport && <ImportedStubCard importId={viewImport} onClose={() => setViewImport(null)} />}
      <VacationBankHistoryDialog
        open={!!vacHistory}
        employeeId={vacHistory?.employee_id}
        employeeName={vacHistory?.employee_name}
        onClose={() => setVacHistory(null)}
      />
      {profileId ? (
        <PayrollEmployeeProfile
          employeeId={profileId}
          onClose={() => setProfileId(null)}
          onChanged={load}
        />
      ) : (<>
      {/* Header + YTD gov owed */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8fa4f0]">Payroll</p>
          <h2 className="text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Weekly Payroll</h2>
        </div>
        <div className="flex flex-wrap items-stretch gap-2">
          {/* This week's remittance — live draft total, follows edits even
              before the week's paystubs are saved */}
          <div className="rounded-lg border border-white/15 bg-white/[0.04] px-4 py-2.5" data-testid="gov-owed-week">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">
              This week · owed to gov
            </p>
            <p className="font-mono text-2xl font-bold text-white" data-testid="gov-week-total">{money(weekTotals.gov)}</p>
            <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-white/40" data-testid="gov-week-breakdown">
              {weekTotals.saved}/{rows.length} saved · CPP {money(weekTotals.cpp)} · EI {money(weekTotals.ei)} · Fed {money(weekTotals.fed)} · ON {money(weekTotals.on)}
            </p>
          </div>
          {/* Running unpaid balance since the last marked CRA payment */}
          <div className="theme-keep-bg rounded-lg border border-amber-500/60 bg-amber-400 px-4 py-2.5" data-testid="gov-owed-ytd">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[9px] uppercase tracking-widest text-black/70">
                Total owed to CRA{govOwed.since ? " · since last payment" : ""}
              </p>
              <button
                onClick={markCraPaid}
                disabled={!govOwed.total}
                data-testid="cra-mark-paid-btn"
                className="rounded-md bg-black/85 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
              >
                Paid
              </button>
            </div>
            <p className="font-mono text-2xl font-bold text-black" data-testid="gov-owed-total">{money(govOwed.total)}</p>
            {!!govOwed.paystubs && (
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-black/60">
                {govOwed.paystubs} paystubs · CPP {money(govOwed.breakdown?.cpp)} · EI {money(govOwed.breakdown?.ei)} · Fed {money(govOwed.breakdown?.federal_tax)} · ON {money(govOwed.breakdown?.provincial_tax)}
              </p>
            )}
            {!!govOwed.last_payment && (
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-black/60" data-testid="gov-owed-last-payment">
                Last paid {new Date(govOwed.last_payment.paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · {money(govOwed.last_payment.amount)}
              </p>
            )}
          </div>
        </div>
      </div>

      {anyChanges && (
        <div className="rounded-md border border-red-500/40 bg-red-500/[0.08] px-4 py-2 text-sm text-red-200" data-testid="payroll-dirty-banner">
          Payroll has not been confirmed. Please ensure all entered data is correct before submitting your paystubs.
        </div>
      )}

      {vacRequests.length > 0 && (
        <div className="theme-keep-bg rounded-lg border-2 border-yellow-400 bg-yellow-300 px-4 py-3" data-testid="payroll-vacation-requests">
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-black/80">
            Vacation pay requested
            {vacRequests.some((r) => r.week_start === weekISO) ? " · this pay" : ""}
          </p>
          <div className="mt-1 space-y-0.5">
            {vacRequests.map((r) => (
              <div key={r.request_id} className="flex items-start justify-between gap-2" data-testid={`payroll-vacation-request-${r.request_id}`}>
                <p className="text-sm font-semibold text-black">
                  {r.employee_name} — {Number(r.hours || 0) > 0 ? `${Number(r.hours).toFixed(2)} hrs (≈ ${money(r.amount)})` : money(r.amount)}
                  <span className="font-normal text-black/60">
                    {r.week_start === weekISO
                      ? " · THIS PAY — pre-filled in their vacation payout below, review and save"
                      : ` · for pay week of ${new Date(`${r.week_start}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    {" "}· requested {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                </p>
                <button onClick={() => deleteVacRequest(r)}
                  aria-label={`Delete ${r.employee_name}'s vacation request`}
                  data-testid={`payroll-vacation-request-delete-${r.request_id}`}
                  className="mt-0.5 shrink-0 rounded p-1 text-black/40 transition hover:bg-black/10 hover:text-red-700">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week nav */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="payroll-prev-week"
          className="rounded-md border border-white/15 bg-white/5 p-2 text-white/70 hover:border-white/25 hover:text-white">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <select
          value={weekISO}
          onChange={(ev) => setWeekStart(mondayOf(fromISO(ev.target.value)))}
          data-testid="payroll-range-label"
          title="Jump to another week"
          className="min-w-0 max-w-[calc(100vw-2rem)] flex-shrink truncate rounded-md border border-white/15 bg-white/5 px-4 py-2 font-mono text-xs tracking-widest text-white [color-scheme:dark] hover:border-white/25 focus:outline-none"
        >
          {(pastWeeks.includes(weekISO) ? pastWeeks : [weekISO, ...pastWeeks]).map((w) => (
            <option key={w} value={w}>{fmtRange(fromISO(w))}</option>
          ))}
        </select>
        <button onClick={() => setWeekStart(addDays(weekStart, 7))} disabled={!canGoNext} data-testid="payroll-next-week"
          className={`rounded-md border p-2 ${canGoNext
            ? "border-white/15 bg-white/5 text-white/70 hover:border-white/25 hover:text-white"
            : "cursor-not-allowed border-white/5 bg-white/[0.02] text-white/20"}`}>
          <ChevronRight className="h-4 w-4" />
        </button>
        <button onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="payroll-this-week"
          className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/70 hover:border-white/25 hover:text-white">
          This week
        </button>
        <button onClick={load} data-testid="payroll-refresh"
          className="rounded-md border border-white/15 bg-white/5 p-2 text-white/70 hover:border-white/25 hover:text-white" title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
        {canAdmin && (
          <>
            <button onClick={() => bulkFileRef.current?.click()} disabled={bulkBusy}
              data-testid="bulk-import-stubs-btn"
              title="Import pay stubs in bulk (PDF or CSV export) — each is scanned and matched to an employee by the name inside"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
              <FileUp className="h-4 w-4" /> {bulkBusy ? "Scanning…" : "Import stubs"}
            </button>
            <input ref={bulkFileRef} type="file" accept="application/pdf,.pdf,text/csv,.csv" multiple className="hidden"
              data-testid="bulk-import-file" onChange={bulkImport} />
          </>
        )}
        {/* View toggle: Cards / Grid — auto-defaults to cards on phones and
            grid on tablets/laptops, remembered per-user (mirrors Hours tab) */}
        <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-black/15 bg-black/[0.04] dark:border-white/15 dark:bg-white/5" data-testid="payroll-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            data-testid="payroll-view-cards"
            aria-pressed={effectiveMode === "cards"}
            title="Card view (one employee at a time)"
            className={`flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
              effectiveMode === "cards"
                ? "bg-[#1a3fd1] text-white"
                : "text-slate-600 hover:text-slate-900 dark:text-white/60 dark:hover:text-white"
            }`}
          >
            <Rows3 className="h-3.5 w-3.5" /> Cards
          </button>
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            data-testid="payroll-view-grid"
            aria-pressed={effectiveMode === "grid"}
            title="Grid view (full payroll table)"
            className={`flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-widest transition ${
              effectiveMode === "grid"
                ? "bg-[#1a3fd1] text-white"
                : "text-slate-600 hover:text-slate-900 dark:text-white/60 dark:hover:text-white"
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Grid
          </button>
        </div>
      </div>

      {/* Totals bar */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="payroll-week-totals">
          <TotalPill label="Gross" value={money(weekTotals.gross)} />
          <TotalPill label="Net" value={recalculating ? "…" : money(weekTotals.net)} tone="emerald" />
          <TotalPill label="Gov owed" value={recalculating ? "…" : money(weekTotals.gov)} tone="amber" />
          <TotalPill label="Stubs" value={`${weekTotals.saved} / ${rows.length}`} />
        </div>
      )}

      {/* 8-hour cap & overtime report — starts collapsed; More/Less toggles */}
      {!loading && capReport.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06]" data-testid="cap-report-card">
          <button
            type="button"
            onClick={() => setCapOpen((v) => !v)}
            data-testid="cap-report-toggle"
            aria-expanded={capOpen}
            className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300">8h cap &amp; overtime this week</p>
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-200/70">
              {capReport.length} employee{capReport.length === 1 ? "" : "s"} · +{capReport.reduce((s, r) => s + (r.total_over_hours || 0), 0).toFixed(2)}h extra
              {capOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {capOpen ? "Less" : "More"}
            </span>
          </button>
          {capOpen && (
          <div className="divide-y divide-white/5 border-t border-amber-500/20">
            {capReport.map((r) => (
              <div key={r.employee_id} className="px-4 py-2.5" data-testid={`cap-report-row-${r.employee_id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-sm font-semibold text-white">{r.name}</span>
                  <span className="font-mono text-[11px] text-amber-200">
                    {r.days_over} {r.days_over === 1 ? "day" : "days"} over 8h · +{r.total_over_hours.toFixed(2)}h extra
                  </span>
                  <span className="flex gap-1.5 font-mono text-[10px] uppercase tracking-widest">
                    {r.ot_approved > 0 && <span className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300">{r.ot_approved} approved</span>}
                    {r.ot_denied > 0 && <span className="rounded border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-red-300">{r.ot_denied} denied</span>}
                    {r.ot_pending > 0 && <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-amber-300">{r.ot_pending} pending</span>}
                  </span>
                </div>
                <p className="mt-1 font-mono text-[10px] text-white/40">
                  {r.days.map((d) => {
                    const dt = new Date(`${d.day}T12:00:00`);
                    return `${dt.toLocaleDateString("en-US", { weekday: "short" })} ${d.raw_hours.toFixed(2)}h→${d.paid_hours.toFixed(2)}h${d.ot_status !== "none" ? ` (${d.ot_status})` : ""}`;
                  }).join(" · ")}
                </p>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Spreadsheet table (grid) or per-employee cards — per view toggle */}
      {loading ? (
        <p className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/50">Loading payroll…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/50">No active employees to pay.</p>
      ) : effectiveMode === "grid" ? (
        <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="payroll-table-wrap">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-black/40 text-white/60">
                <th rowSpan={2} className="border-b border-r border-white/10 px-2 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest">Employee</th>
                <th rowSpan={2} className="border-b border-r border-white/10 px-1 py-1.5 text-left font-mono text-[9px] uppercase tracking-widest">Location</th>
                <th colSpan={1} className="border-b border-r border-white/10 py-0.5 text-center font-mono text-[9px] uppercase tracking-widest">Salary</th>
                <th colSpan={2} className="border-b border-r border-white/10 py-0.5 text-center font-mono text-[9px] uppercase tracking-widest">Hourly</th>
                <th colSpan={3} className="border-b border-r border-white/10 py-0.5 text-center font-mono text-[9px] uppercase tracking-widest">Vacation</th>
                <th rowSpan={2} className="border-b border-r border-white/10 px-1 py-1.5 text-center font-mono text-[9px] uppercase tracking-widest">Holiday</th>
                <th rowSpan={2} className="border-b border-r border-white/10 px-1 py-1.5 text-center font-mono text-[9px] uppercase tracking-widest">CRA</th>
                <th rowSpan={2} className="border-b border-r border-white/10 px-2 py-1.5 text-right font-mono text-[9px] uppercase tracking-widest">Gross</th>
                <th rowSpan={2} className="border-b border-white/10 px-2 py-1.5 text-right font-mono text-[9px] uppercase tracking-widest text-emerald-800 dark:text-emerald-300">Net</th>
              </tr>
              <tr className="bg-black/25 text-white/40">
                <th className="border-b border-r border-white/10 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">$/wk</th>
                <th className="border-b border-r border-white/5 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">Hrs</th>
                <th className="border-b border-r border-white/10 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">Rate</th>
                <th className="border-b border-r border-white/5 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">Bank $</th>
                <th className="border-b border-r border-white/5 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">Pay $</th>
                <th className="border-b border-r border-white/10 px-1 py-0.5 text-center font-mono text-[8px] uppercase tracking-widest">Hrs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const e = edits[r.employee_id] || {};
                const gross = rowGross(r);
                const over = overVac(r);
                const isExp = expanded === r.employee_id;
                const isSalary = r.pay_type === "salary";
                return (
                  <Fragment key={r.employee_id}>
                    <tr
                      data-testid={`paystub-row-${r.employee_id}`}
                      className={`${i % 2 ? "bg-white/[0.015]" : ""} border-b border-white/5`}>
                      <td className="border-r border-white/5 px-2 py-1.5">
                        <button type="button" onClick={() => setExpanded(isExp ? null : r.employee_id)}
                          className="flex items-center gap-1 text-left font-medium text-white/90 hover:text-[#8fa4f0]"
                          data-testid={`paystub-expand-${r.employee_id}`}>
                          <ChevronDown className={`h-3 w-3 text-white/40 transition-transform ${isExp ? "rotate-180" : ""}`} />
                          <span>{r.employee_name}</span>
                        </button>
                        {canAdmin && (
                          <button
                            type="button"
                            onClick={() => setProfileId(r.employee_id)}
                            aria-label={`Open ${r.employee_name}'s payroll profile`}
                            title="Open payroll profile"
                            data-testid={`payroll-profile-edit-${r.employee_id}`}
                            className="ml-1 inline-flex rounded p-0.5 text-white/35 hover:bg-white/10 hover:text-white"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                        {!!r.vacation_exempt && (
                          <span className="ml-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-px font-mono text-[8px] font-bold uppercase tracking-widest text-amber-300" data-testid={`vac-exempt-${r.employee_id}`}>
                            Vacation exempt
                          </span>
                        )}
                        {!r.vacation_exempt && !!r.vacation_opted_out && (
                          <span className="ml-1 rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-px font-mono text-[8px] font-bold uppercase tracking-widest text-sky-600 dark:text-sky-300" data-testid={`vac-payout-${r.employee_id}`}>
                            Vacation paid out
                          </span>
                        )}
                      </td>
                      <td className="border-r border-white/5 px-1 py-1.5 text-white/70" data-testid={`paystub-location-${r.employee_id}`}>
                        {r.location}
                      </td>
                      {/* Salary weekly $ */}
                      <td className="border-r border-white/5 px-1 py-1.5 text-center font-mono text-white/80">
                        {isSalary ? money(r.weekly_salary) : <span className="text-white/20">—</span>}
                      </td>
                      {/* Hourly hrs */}
                      <td className="border-r border-white/5 px-1 py-1.5 text-center font-mono text-white/80">
                        {isSalary ? <span className="text-white/20">—</span>
                          : (Number(r.hours_worked || 0) + Number(r.stat_holiday_hours_total || 0)).toFixed(2)}
                      </td>
                      {/* Hourly rate — per-week picker when a perfect-week
                          bonus rate exists (nothing auto-decides anymore) */}
                      <td className="border-r border-white/5 px-1 py-1.5 text-center font-mono text-white/80">
                        {isSalary ? <span className="text-white/20">—</span>
                          : hasPerfectRate(r) && canAdmin ? (
                            <select
                              value={e.rate_choice || r.rate_choice_applied || "base"}
                              disabled={rateSaving === r.employee_id}
                              onChange={(ev) => setRateChoice(r.employee_id, ev.target.value)}
                              title={rateSaving === r.employee_id ? "Saving…"
                                : "Pick which rate to pay this week — saves immediately and becomes this employee's rate for future weeks (past weeks never change)"}
                              data-testid={`rate-choice-${r.employee_id}`}
                              className="w-28 rounded border border-white/10 bg-white/[0.03] px-0.5 py-0.5 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none"
                            >
                              <option value="base">{money(r.hourly_rate_base)}</option>
                              <option value="perfect">{money(r.hourly_rate_perfect)}</option>
                            </select>
                          ) : money(r.hourly_rate)}
                      </td>
                      {/* Vac banked */}
                      <td className="border-r border-white/5 px-1 py-1.5 text-center font-mono text-emerald-800 dark:text-emerald-300" data-testid={`vac-banked-${r.employee_id}`}>
                        <VacBankCell
                          employeeId={r.employee_id}
                          value={r.vacation_available_before}
                          accrued={r.vacation_pay_accrued}
                          paidOut={r.vacation_paid_out}
                          canEdit={canAdmin}
                          onSaved={(eid, avail, availBefore) => setRows((rs) => rs.map((x) => (x.employee_id === eid ? { ...x, vacation_available_before: availBefore ?? avail, vacation_payable: Number(availBefore ?? avail) + Number(x.vacation_pay_accrued || 0) - Number(x.vacation_auto_paid || 0) } : x)))}
                        />
                        {canAdmin && !r.vacation_exempt && (
                          <button type="button" onClick={() => setVacHistory({ employee_id: r.employee_id, employee_name: r.employee_name })}
                            title="Banked vacation history — every week's accrual and payout that built this number"
                            aria-label="Banked vacation history" data-testid={`vac-history-btn-${r.employee_id}`}
                            className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded border border-emerald-500/30 text-emerald-700 transition-colors hover:border-emerald-400 hover:text-emerald-500 dark:text-emerald-300/70 dark:hover:text-emerald-200">
                            <History className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                      {/* Vac payout $ */}
                      <td className="border-r border-white/5 px-1 py-1">
                        <input type="text" inputMode="decimal"
                          value={r.vacation_exempt ? "" : (e.vac_dollars || "")}
                          onChange={(ev) => editRow(r.employee_id, "vac_dollars", ev.target.value)}
                          disabled={!!r.vacation_exempt}
                          title={r.vacation_exempt ? "Vacation exempt — owner role: never accrued or paid"
                            : r.vacation_opted_out ? `Vacation waiver on file — $${Number(r.vacation_auto_paid || 0).toFixed(2)} accrual is paid out automatically on this pay. Type an amount here to ALSO pay out banked vacation (max $${Math.max(0, payableBank(r)).toFixed(2)}).`
                            : undefined}
                          data-testid={`vac-dollars-${r.employee_id}`}
                          placeholder={r.vacation_exempt ? "EXEMPT" : r.vacation_opted_out ? `AUTO ${Number(r.vacation_auto_paid || 0).toFixed(2)}` : "0"}
                          className={`h-7 w-full rounded border px-1 text-center font-mono text-xs focus:outline-none ${
                            r.vacation_exempt ? "cursor-not-allowed border-amber-500/30 bg-white/[0.02] text-amber-300/60 placeholder:text-amber-300/60" :
                            r.vacation_opted_out && !over ? "border-sky-500/30 bg-white/[0.03] text-white/90 placeholder:text-sky-700/60 focus:border-[#1a3fd1] dark:placeholder:text-sky-300/50" :
                            over ? "border-red-500/60 bg-white/[0.03] text-white/90 focus:border-red-500" : "border-white/10 bg-white/[0.03] text-white/90 focus:border-[#1a3fd1]"
                          }`} />
                      </td>
                      {/* Vac hrs */}
                      <td className="border-r border-white/5 px-1 py-1">
                        <div className="relative">
                          <input type="text" inputMode="decimal"
                            value={r.vacation_exempt ? "" : (e.vac_hrs || "")}
                            onChange={(ev) => editRow(r.employee_id, "vac_hrs", ev.target.value)}
                            disabled={!!r.vacation_exempt}
                            title={r.vacation_exempt ? "Vacation exempt — owner role: never accrued or paid"
                              : r.vacation_opted_out ? `Vacation waiver on file — ${Number(r.vacation_auto_hours || 0).toFixed(2)} h of accrual is paid out automatically on this pay. Type hours here to ALSO pay out banked vacation.`
                              : undefined}
                            onBlur={(ev) => {
                              const n = parseFloat(ev.target.value);
                              if (!isNaN(n)) {
                                const s = String(Math.round(n * 4) / 4);
                                if (s !== ev.target.value) {
                                  editRow(r.employee_id, "vac_hrs", s);
                                  toast.info(`Snapped to ${s} hrs`, { duration: 2500 });
                                }
                              }
                            }}
                            data-testid={`vac-hours-${r.employee_id}`}
                            placeholder={r.vacation_exempt ? "EXEMPT" : r.vacation_opted_out ? `AUTO ${Number(r.vacation_auto_hours || 0).toFixed(2)}` : "0"}
                            className={`h-7 w-full rounded border pl-1 pr-4 text-center font-mono text-xs focus:outline-none ${
                              r.vacation_exempt ? "cursor-not-allowed border-amber-500/30 bg-white/[0.02] text-amber-300/60 placeholder:text-amber-300/60" :
                              r.vacation_opted_out && !over ? "border-sky-500/30 bg-white/[0.03] text-white/90 placeholder:text-sky-700/60 focus:border-[#1a3fd1] dark:placeholder:text-sky-300/50" :
                              over ? "border-red-500/60 bg-white/[0.03] text-white/90 focus:border-red-500" : "border-white/10 bg-white/[0.03] text-white/90 focus:border-[#1a3fd1]"
                            }`} />
                          {over && (
                            <button type="button" onClick={() => setToBanked(r)}
                              title={`Payout $${rowPayout$(r).toFixed(2)} exceeds the $${payableBank(r).toFixed(2)} banked (confirmed + this week's accrual). Click to set the payout to the banked amount.`}
                              data-testid={`vac-warn-${r.employee_id}`}
                              aria-label="Set vacation payout to banked amount"
                              className="absolute right-0.5 top-1/2 -translate-y-1/2 rounded text-red-400 transition-colors hover:text-red-200">
                              <AlertCircle className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      {/* Holiday pay */}
                      <td className="border-r border-white/5 px-1 py-1">
                        <input type="text" inputMode="decimal" value={e.holiday || ""}
                          onChange={(ev) => editRow(r.employee_id, "holiday", ev.target.value)}
                          data-testid={`holiday-pay-${r.employee_id}`}
                          placeholder="0"
                          className="h-7 w-full rounded border border-white/10 bg-white/[0.03] px-1 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none" />
                      </td>
                      {/* CRA repayment */}
                      <td className="border-r border-white/5 px-1 py-1">
                        <input type="text" inputMode="decimal" value={e.cra || ""}
                          onChange={(ev) => editRow(r.employee_id, "cra", ev.target.value)}
                          data-testid={`cra-repay-${r.employee_id}`}
                          placeholder="0"
                          className="h-7 w-full rounded border border-white/10 bg-white/[0.03] px-1 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none" />
                      </td>
                      {/* Gross */}
                      <td className="border-r border-white/5 px-2 py-1.5 text-right font-mono text-white" data-testid={`gross-pay-${r.employee_id}`}>
                        {money(gross)}
                      </td>
                      {/* Net (server-computed) */}
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-800 dark:text-emerald-300" data-testid={`net-pay-cell-${r.employee_id}`}>
                        {calcBusy[r.employee_id] ? <span className="text-white/40">…</span> : money(r.net_pay)}
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="border-b border-white/10 bg-white/[0.02]">
                        <td colSpan={12} className="px-4 py-3">
                          {renderStubDetails(r, gross)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-white/20 bg-black/40 font-mono text-[10px] font-bold tracking-widest text-white/85" data-testid="payroll-totals-row">
                <td className="border-r border-white/10 px-2 py-1.5 text-left uppercase">Totals</td>
                <td className="border-r border-white/10 px-1 py-1.5"></td>
                <td className="border-r border-white/10 px-1 py-1.5 text-center">{money(rows.reduce((s, r) => s + (r.pay_type === "salary" ? Number(r.weekly_salary || 0) : 0), 0))}</td>
                <td className="border-r border-white/5 px-1 py-1.5 text-center">{rows.reduce((s, r) => s + Number(r.hours_worked || 0), 0).toFixed(2)}</td>
                <td className="border-r border-white/10 px-1 py-1.5"></td>
                <td className="border-r border-white/5 px-1 py-1.5 text-center text-emerald-800 dark:text-emerald-300" title="Live banked vacation: confirmed + this week's accrual − this week's payout">{money(rows.reduce((s, r) => s + Number(r.vacation_available_before || 0) + Number(r.vacation_pay_accrued || 0) - Number(r.vacation_paid_out || 0), 0))}</td>
                <td className="border-r border-white/5 px-1 py-1.5 text-center">{money(rows.reduce((s, r) => s + rowPayout$(r), 0))}</td>
                <td className="border-r border-white/10 px-1 py-1.5 text-center">{rows.reduce((s, r) => s + num((edits[r.employee_id] || {}).vac_hrs), 0).toFixed(2)}</td>
                <td className="border-r border-white/10 px-1 py-1.5 text-center">{money(rows.reduce((s, r) => s + num((edits[r.employee_id] || {}).holiday), 0))}</td>
                <td className="border-r border-white/10 px-1 py-1.5 text-center">{money(rows.reduce((s, r) => s + num((edits[r.employee_id] || {}).cra), 0))}</td>
                <td className="border-r border-white/10 px-2 py-1.5 text-right">{money(rows.reduce((s, r) => s + rowGross(r), 0))}</td>
                <td className="px-2 py-1.5 text-right text-emerald-800 dark:text-emerald-300">{money(rows.reduce((s, r) => s + Number(r.net_pay || 0), 0))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        /* Card view (default on phones): collapsed shows name + net; expanding
           reveals every grid field plus the same paystub breakdown the grid's
           row-expander shows. */
        <div className="space-y-2" data-testid="payroll-cards">
          {rows.map((r) => {
            const e = edits[r.employee_id] || {};
            const gross = rowGross(r);
            const over = overVac(r);
            const isSalary = r.pay_type === "salary";
            const vacInputCls = `h-8 w-full rounded border px-2 text-center font-mono text-xs focus:outline-none ${
              r.vacation_exempt ? "cursor-not-allowed border-amber-500/30 bg-white/[0.02] text-amber-300/60 placeholder:text-amber-300/60" :
              r.vacation_opted_out ? "cursor-not-allowed border-sky-500/30 bg-white/[0.02] text-sky-700/70 placeholder:text-sky-700/50 dark:text-sky-300/70 dark:placeholder:text-sky-300/50" :
              over ? "border-red-500/60 bg-white/[0.03] text-white/90 focus:border-red-500" : "border-white/10 bg-white/[0.03] text-white/90 focus:border-[#1a3fd1]"
            }`;
            return (
              <details key={r.employee_id}
                className="group rounded-lg border border-white/10 bg-white/[0.02] open:bg-white/[0.04]"
                data-testid={`payroll-card-${r.employee_id}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 [&::-webkit-details-marker]:hidden">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition-transform group-open:rotate-90" />
                    <span className="truncate font-medium text-white/90">{r.employee_name}</span>
                    {!!r.vacation_exempt && (
                      <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-px font-mono text-[8px] font-bold uppercase tracking-widest text-amber-300" data-testid={`vac-exempt-${r.employee_id}`}>
                        Vacation exempt
                      </span>
                    )}
                    {!r.vacation_exempt && !!r.vacation_opted_out && (
                      <span className="rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-px font-mono text-[8px] font-bold uppercase tracking-widest text-sky-600 dark:text-sky-300" data-testid={`vac-payout-${r.employee_id}`}>
                        Vacation paid out
                      </span>
                    )}
                    {!!r.saved && (
                      <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest text-emerald-300">Saved</span>
                    )}
                  </div>
                  <span className="shrink-0 font-mono text-sm text-emerald-800 dark:text-emerald-300" data-testid={`payroll-card-net-${r.employee_id}`}>
                    {calcBusy[r.employee_id] ? "…" : money(r.net_pay)}
                  </span>
                  {canAdmin && (
                    <button type="button"
                      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); setProfileId(r.employee_id); }}
                      aria-label={`Open ${r.employee_name}'s payroll profile`}
                      title="Payroll profile"
                      data-testid={`payroll-profile-edit-${r.employee_id}`}
                      className="shrink-0 rounded p-1 text-white/50 transition hover:bg-white/10 hover:text-white">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </summary>
                <div className="space-y-3 border-t border-white/5 p-3">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <CardStat label="Location" testid={`paystub-location-${r.employee_id}`}>{r.location}</CardStat>
                    {isSalary ? (
                      <CardStat label="Salary $/wk">{money(r.weekly_salary)}</CardStat>
                    ) : (
                      <>
                        <CardStat label="Hours">{(Number(r.hours_worked || 0) + Number(r.stat_holiday_hours_total || 0)).toFixed(2)}</CardStat>
                        {hasPerfectRate(r) && canAdmin ? (
                          <CardInput label={rateSaving === r.employee_id ? "Rate this week — saving…" : "Rate this week"}>
                            <select
                              value={e.rate_choice || r.rate_choice_applied || "base"}
                              disabled={rateSaving === r.employee_id}
                              onChange={(ev) => setRateChoice(r.employee_id, ev.target.value)}
                              title="Pick which rate to pay this week — your pick becomes this employee's rate for future weeks too (past weeks never change)"
                              data-testid={`rate-choice-${r.employee_id}`}
                              className="h-8 w-full rounded border border-white/10 bg-white/[0.03] px-1 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none"
                            >
                              <option value="base">{money(r.hourly_rate_base)}</option>
                              <option value="perfect">{money(r.hourly_rate_perfect)}</option>
                            </select>
                          </CardInput>
                        ) : (
                          <CardStat label="Rate">{money(r.hourly_rate)}</CardStat>
                        )}
                      </>
                    )}
                    <CardInput label="Banked vac $">
                      <VacBankCell
                        employeeId={r.employee_id}
                        value={r.vacation_available_before}
                        accrued={r.vacation_pay_accrued}
                        paidOut={r.vacation_paid_out}
                        canEdit={canAdmin}
                        onSaved={(eid, avail, availBefore) => setRows((rs) => rs.map((x) => (x.employee_id === eid ? { ...x, vacation_available_before: availBefore ?? avail, vacation_payable: Number(availBefore ?? avail) + Number(x.vacation_pay_accrued || 0) - Number(x.vacation_auto_paid || 0) } : x)))}
                      />
                      {canAdmin && !r.vacation_exempt && (
                        <button type="button" onClick={() => setVacHistory({ employee_id: r.employee_id, employee_name: r.employee_name })}
                          title="Banked vacation history — every week's accrual and payout that built this number"
                          aria-label="Banked vacation history" data-testid={`vac-history-btn-${r.employee_id}`}
                          className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded border border-emerald-500/30 text-emerald-700 transition-colors hover:border-emerald-400 hover:text-emerald-500 dark:text-emerald-300/70 dark:hover:text-emerald-200">
                          <History className="h-3 w-3" />
                        </button>
                      )}
                    </CardInput>
                    <CardInput label="Vacation pay $">
                      <input type="text" inputMode="decimal"
                        value={r.vacation_exempt ? "" : (e.vac_dollars || "")}
                        onChange={(ev) => editRow(r.employee_id, "vac_dollars", ev.target.value)}
                        disabled={!!r.vacation_exempt}
                        title={r.vacation_exempt ? "Vacation exempt — owner role: never accrued or paid"
                          : r.vacation_opted_out ? `Vacation waiver on file — $${Number(r.vacation_auto_paid || 0).toFixed(2)} accrual is paid out automatically on this pay. Type an amount here to ALSO pay out banked vacation (max $${Math.max(0, payableBank(r)).toFixed(2)}).`
                          : undefined}
                        data-testid={`vac-dollars-${r.employee_id}`}
                        placeholder={r.vacation_exempt ? "EXEMPT" : r.vacation_opted_out ? `AUTO ${Number(r.vacation_auto_paid || 0).toFixed(2)}` : "0"}
                        className={vacInputCls} />
                    </CardInput>
                    <CardInput label="Vacation hrs">
                      <div className="relative">
                        <input type="text" inputMode="decimal"
                          value={r.vacation_exempt ? "" : (e.vac_hrs || "")}
                          onChange={(ev) => editRow(r.employee_id, "vac_hrs", ev.target.value)}
                          disabled={!!r.vacation_exempt}
                          title={r.vacation_exempt ? "Vacation exempt — owner role: never accrued or paid"
                            : r.vacation_opted_out ? `Vacation waiver on file — ${Number(r.vacation_auto_hours || 0).toFixed(2)} h of accrual is paid out automatically on this pay. Type hours here to ALSO pay out banked vacation.`
                            : undefined}
                          onBlur={(ev) => {
                            const n = parseFloat(ev.target.value);
                            if (!isNaN(n)) {
                              const s = String(Math.round(n * 4) / 4);
                              if (s !== ev.target.value) {
                                editRow(r.employee_id, "vac_hrs", s);
                                toast.info(`Snapped to ${s} hrs`, { duration: 2500 });
                              }
                            }
                          }}
                          data-testid={`vac-hours-${r.employee_id}`}
                          placeholder={r.vacation_exempt ? "EXEMPT" : r.vacation_opted_out ? `AUTO ${Number(r.vacation_auto_hours || 0).toFixed(2)}` : "0"}
                          className={`${vacInputCls} pr-5`} />
                        {over && (
                          <button type="button" onClick={() => setToBanked(r)}
                            title={`Payout exceeds the $${payableBank(r).toFixed(2)} banked (confirmed + this week's accrual). Click to set the payout to the banked amount.`}
                            data-testid={`vac-warn-${r.employee_id}`}
                            aria-label="Set vacation payout to banked amount"
                            className="absolute right-1 top-1/2 -translate-y-1/2 rounded text-red-400 transition-colors hover:text-red-200">
                            <AlertCircle className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </CardInput>
                    <CardInput label="Holiday $">
                      <input type="text" inputMode="decimal" value={e.holiday || ""}
                        onChange={(ev) => editRow(r.employee_id, "holiday", ev.target.value)}
                        data-testid={`holiday-pay-${r.employee_id}`}
                        placeholder="0"
                        className="h-8 w-full rounded border border-white/10 bg-white/[0.03] px-2 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none" />
                    </CardInput>
                    <CardInput label="CRA $">
                      <input type="text" inputMode="decimal" value={e.cra || ""}
                        onChange={(ev) => editRow(r.employee_id, "cra", ev.target.value)}
                        data-testid={`cra-repay-${r.employee_id}`}
                        placeholder="0"
                        className="h-8 w-full rounded border border-white/10 bg-white/[0.03] px-2 text-center font-mono text-xs text-white/90 focus:border-[#1a3fd1] focus:outline-none" />
                    </CardInput>
                    <CardStat label="Gross" testid={`gross-pay-${r.employee_id}`}>{money(gross)}</CardStat>
                    <CardStat label="Net" tone="emerald" testid={`net-pay-cell-${r.employee_id}`}>
                      {calcBusy[r.employee_id] ? "…" : money(r.net_pay)}
                    </CardStat>
                  </div>
                  {renderStubDetails(r, gross)}
                </div>
              </details>
            );
          })}
        </div>
      )}

      {/* Save-all */}
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <p className="mr-auto font-mono text-[10px] uppercase tracking-widest text-white/40">
            Editable: rate, vacation, holiday pay, CRA repayment. Hours & wages come from the Hours tab. Saved weeks can be re-saved — every figure updates everywhere.
          </p>
          <button type="button" onClick={saveAll} disabled={saving || anyOver}
            data-testid="payroll-save-all"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white ${
              anyOver ? "cursor-not-allowed bg-white/10 text-white/40" : "bg-[#1a3fd1] hover:bg-[#1a3fd1]/85"
            }`}>
            <Save className="h-4 w-4" />
            {saving ? "Saving payroll…" : "Confirm & save payroll"}
          </button>
        </div>
      )}

      {/* Employee profile opens as a child of the payroll tab, not an overlay */}
      </>)}

      {/* Bulk Knit stub import — per-file results after scanning */}
      {bulkResults && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="bulk-import-results">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-[#0d0d0d]">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-900 dark:text-white">
                Stub import — {bulkResults.imported} of {bulkResults.total} imported{bulkResults.duplicates ? ` · ${bulkResults.duplicates} duplicate${bulkResults.duplicates === 1 ? "" : "s"} skipped` : ""}
              </h3>
              <button onClick={() => setBulkResults(null)} data-testid="bulk-import-close"
                className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 dark:border-white/20 dark:text-white/70 dark:hover:bg-white/10">
                Close
              </button>
            </div>
            <p className="mt-1 font-mono text-[10px] text-gray-500 dark:text-white/45">
              Tip: you can also drop PDFs, CSVs or whole folders anywhere on the Payroll page.
            </p>
            <div className="mt-3 space-y-1.5">
              {bulkResults.results.map((r, i) => (
                <div key={i} data-testid={`bulk-import-row-${i}`}
                  className={`rounded-md border px-3 py-2 text-xs ${
                    r.status === "imported"
                      ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-200"
                      : r.status === "duplicate"
                        ? "border-sky-500/40 bg-sky-500/5 text-sky-900 dark:text-sky-200"
                        : r.status === "unmatched"
                          ? "border-amber-500/40 bg-amber-500/5 text-amber-900 dark:text-amber-200"
                          : "border-red-500/40 bg-red-500/5 text-red-900 dark:text-red-200"
                  }`}>
                  <p className="flex flex-wrap items-center gap-1.5 font-semibold">
                    {r.filename}
                    {r.status === "duplicate" && <span className="rounded-full bg-sky-500/20 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider">Already on file</span>}
                    {r.blank && (r.status === "imported" || r.status === "duplicate") && <span className="rounded-full bg-[#fde68a] px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-[#451a03] dark:bg-[#5a4208] dark:text-[#fde68a]" data-testid={`bulk-import-blank-${i}`}>Blank $0 stub</span>}
                    {r.employee_status && r.employee_status !== "Active" && <span className="rounded-full bg-gray-500/20 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider">{r.employee_status} employee</span>}
                  </p>
                  <p className="mt-0.5 font-mono text-[11px] opacity-80">
                    {r.status === "imported"
                      ? `${r.employee_name} · ${r.label} · gross $${Number(r.gross || 0).toFixed(2)}${r.manually_assigned ? " · manually assigned" : ""}`
                      : r.status === "duplicate"
                        ? `${r.employee_name} · ${r.label} — ${r.detail}`
                        : r.status === "unmatched"
                          ? `${r.stub_name ? `Stub says "${r.stub_name}" — ` : ""}${r.detail}.`
                          : r.detail}
                  </p>
                  {(r.status === "imported" || r.status === "duplicate") && r.import_id && (
                    <button type="button" onClick={() => setViewImport(r.import_id)} data-testid={`bulk-import-view-${i}`}
                      className="mt-1 font-mono text-[10px] font-semibold underline-offset-2 hover:underline">
                      Open stub
                    </button>
                  )}
                  {r.status === "unmatched" && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <select
                        value={assignSel[i] || ""}
                        onChange={(e) => setAssignSel((s) => ({ ...s, [i]: e.target.value }))}
                        data-testid={`bulk-import-assign-${i}`}
                        className="min-w-0 flex-1 rounded-md border border-amber-500/40 bg-white px-2 py-1 font-mono text-[11px] text-gray-900 dark:bg-white/10 dark:text-white"
                      >
                        <option value="">Assign to employee…</option>
                        {bulkEmps.map((e) => (
                          <option key={e.employee_id} value={e.employee_id}>{e.name}{(e.status || "Active") !== "Active" ? ` (${e.status})` : ""}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => assignBulkStub(i)}
                        disabled={!assignSel[i] || assigning[i]}
                        data-testid={`bulk-import-assign-save-${i}`}
                        className="shrink-0 rounded-md bg-[#1a3fd1] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-40"
                      >
                        {assigning[i] ? "…" : "Import"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {bulkResults.results.some((r) => r.status === "unmatched" || r.status === "error") && (
              <p className="mt-3 font-mono text-[10px] text-gray-500 dark:text-white/40">
                Unmatched files aren&apos;t imported until you assign them to an employee above.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CardStat = ({ label, children, tone, testid }) => (
  <div>
    <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</p>
    <p className={`mt-0.5 font-mono text-sm ${tone === "emerald" ? "text-emerald-800 dark:text-emerald-300" : "text-white/85"}`} data-testid={testid}>
      {children}
    </p>
  </div>
);

const CardInput = ({ label, children }) => (
  <div>
    <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</p>
    <div className="mt-0.5">{children}</div>
  </div>
);

const TotalPill = ({ label, value, tone }) => {
  const t = tone === "emerald" ? "border-emerald-500/30 text-emerald-900 dark:text-emerald-200"
    : tone === "amber" ? "border-amber-500/30 text-amber-900 dark:text-amber-200"
    : "border-white/10 text-white/80";
  return (
    <div className={`rounded-md border bg-white/[0.02] px-3 py-2 ${t}`}>
      <p className="font-mono text-[9px] uppercase tracking-widest opacity-70">{label}</p>
      <p className="mt-0.5 font-mono text-base">{value}</p>
    </div>
  );
};

const DetailBox = ({ label, value, tone, testid }) => {
  const t = tone === "emerald" ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-200"
    : tone === "amber" ? "border-amber-500/40 bg-amber-500/5 text-amber-900 dark:text-amber-200"
    : "border-white/15 bg-white/5 text-white/90";
  return (
    <div className={`rounded-md border px-3 py-2 ${t}`} data-testid={testid}>
      <p className="font-mono text-[9px] uppercase tracking-widest opacity-75">{label}</p>
      <p className="mt-0.5 font-mono text-lg">{value}</p>
    </div>
  );
};

const Line = ({ label, v, tone }) => {
  const c = tone === "red" ? "text-red-800 dark:text-red-300"
    : tone === "amber" ? "text-amber-800 dark:text-amber-300"
    : tone === "emerald" ? "text-emerald-800 dark:text-emerald-300"
    : "text-white/70";
  return (
    <div className="flex justify-between gap-2">
      <span className="text-white/50">{label}</span>
      <span className={`font-mono ${c}`}>{money(v)}</span>
    </div>
  );
};

export default PayrollTab;
